'use client'

/**
 * Providers Component
 *
 * Consolidates client-side providers. Order matters (outer to inner):
 * 1. RuntimeConfigProvider - runtime config
 * 2. PostHogProvider - analytics
 * 3. QueryProvider - React Query (data layer); must be above AuthSyncProvider
 * 4. ThemeProvider - theme context
 * 5. AuthSyncProvider - session restoration without blocking first paint
 *
 * Toaster is deferred (500ms or first interaction) so it does not block first paint;
 * Sonner does not queue toasts when unmounted, so a short fallback avoids lost toasts.
 *
 * Sentry client init is in instrumentation-client.ts (before hydration).
 */

import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { PostHogProvider } from '@/providers/PostHogProvider'
import QueryProvider from '@/providers/QueryProvider'
import { RuntimeConfigProvider } from '@/providers/RuntimeConfigProvider'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { AuthSyncProvider } from '@/providers/HydrationProvider'
import type { RuntimePublicConfig } from '@/src/lib/runtime-public-config.shared'

const DeferredToaster = dynamic(
  () => import('sonner').then((mod) => mod.Toaster),
  { ssr: false }
)
const SurveyModal = dynamic(
  () =>
    import('@/components/Modals/SurveyModal').then((mod) => mod.SurveyModal),
  { ssr: false }
)
const PopupCampaignModal = dynamic(
  () =>
    import('@/components/Modals/PopupCampaignModal').then(
      (mod) => mod.PopupCampaignModal
    ),
  { ssr: false }
)
const TOASTER_REVEAL_EVENTS = [
  'pointerdown',
  'keydown',
  'scroll',
  'touchstart',
] as const
// 500ms fallback so Toaster is ready before validation toasts; Sonner does not queue when unmounted.
const TOASTER_FALLBACK_DELAY_MS = 500
const SONNER_TOAST_STYLES = `
.toast-item[data-state='open'] {
  animation: slideInFromTop 300ms cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-item[data-state='closed'] {
  animation: slideOutToTop 200ms ease-in;
}

[data-sonner-toaster] [data-sonner-toast] {
  border-radius: 0.75rem !important;
  font-family: inherit !important;
}

[data-sonner-toaster] [data-sonner-toast][data-type='success'],
[data-sonner-toast][data-type='success'] {
  --normal-bg: #fefcf3 !important;
  --normal-border: #d4af37 !important;
  --normal-text: #166534 !important;
  background: #fefcf3 !important;
  border: 1px solid #d4af37 !important;
  color: #166534 !important;
}

[data-sonner-toast][data-type='success'] [data-icon],
[data-sonner-toast][data-type='success'] svg {
  color: #16a34a !important;
}

[data-sonner-toaster] [data-sonner-toast][data-type='error'],
[data-sonner-toast][data-type='error'] {
  background: #fef2f2 !important;
  border: 1px solid #fecaca !important;
  color: #991b1b !important;
}

[data-sonner-toaster] [data-sonner-toast][data-type='warning'],
[data-sonner-toast][data-type='warning'] {
  background: #fffbeb !important;
  border: 1px solid #fde68a !important;
  color: #92400e !important;
}

[data-sonner-toaster] [data-sonner-toast][data-type='info'],
[data-sonner-toast][data-type='info'] {
  background: var(--dday-champagne-50) !important;
  border: 1px solid var(--dday-champagne-300) !important;
  color: var(--dday-champagne-800) !important;
}

.dark [data-sonner-toast][data-type='success'],
html.dark [data-sonner-toast][data-type='success'] {
  background: #2d2518 !important;
  border: 1px solid #b8941f !important;
  color: #86efac !important;
}

.dark [data-sonner-toast][data-type='success'] [data-icon],
.dark [data-sonner-toast][data-type='success'] svg,
html.dark [data-sonner-toast][data-type='success'] [data-icon],
html.dark [data-sonner-toast][data-type='success'] svg {
  color: #4ade80 !important;
}

.dark [data-sonner-toast][data-type='error'],
html.dark [data-sonner-toast][data-type='error'] {
  background: #450a0a !important;
  border: 1px solid #7f1d1d !important;
  color: #fecaca !important;
}

.dark [data-sonner-toast][data-type='warning'],
html.dark [data-sonner-toast][data-type='warning'] {
  background: #451a03 !important;
  border: 1px solid #78350f !important;
  color: #fde68a !important;
}

.dark [data-sonner-toast][data-type='info'],
html.dark [data-sonner-toast][data-type='info'] {
  background: var(--dday-champagne-900) !important;
  border: 1px solid var(--dday-champagne-700) !important;
  color: var(--dday-champagne-100) !important;
}

@keyframes slideInFromTop {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideOutToTop {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100%);
    opacity: 0;
  }
}
`

interface ProvidersProps {
  children: React.ReactNode
  runtimeConfig: RuntimePublicConfig
}

export function Providers({ children, runtimeConfig }: ProvidersProps) {
  const [showToaster, setShowToaster] = useState(false)

  useEffect(() => {
    let scheduled = false

    const revealToaster = () => {
      if (scheduled) return
      scheduled = true
      cleanup()
      setShowToaster(true)
    }

    // Keep toast UI out of the critical path, but still guarantee availability.
    const timeoutId = setTimeout(revealToaster, TOASTER_FALLBACK_DELAY_MS)

    const cleanup = () => {
      clearTimeout(timeoutId)
      TOASTER_REVEAL_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, revealToaster, true)
      )
    }

    TOASTER_REVEAL_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, revealToaster, {
        once: true,
        capture: true,
        passive: true,
      })
    )

    return cleanup
  }, [])

  return (
    <RuntimeConfigProvider config={runtimeConfig}>
      <PostHogProvider>
        {/* AuthSyncProvider depends on React Query hooks for session validation,
            so QueryProvider must remain above it until auth bootstrap is refactored. */}
        <QueryProvider>
          <ThemeProvider>
            <AuthSyncProvider>{children}</AuthSyncProvider>
            <SurveyModal />
            <PopupCampaignModal />
            {showToaster && (
              <>
                <style>{SONNER_TOAST_STYLES}</style>
                <DeferredToaster position="top-center" closeButton expand />
              </>
            )}
          </ThemeProvider>
        </QueryProvider>
      </PostHogProvider>
    </RuntimeConfigProvider>
  )
}
